/* eslint-disable @typescript-eslint/no-empty-object-type */
import { effect, inject } from '@angular/core';
import {
  patchState,
  SignalStoreFeature,
  signalStoreFeature,
  type,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { firstValueFrom, Observable, pipe, switchMap } from 'rxjs';
import { distinctUntilChanged, filter, tap } from 'rxjs/operators';

import { GqlService } from '@shared/services/gql.service';
import { GlobalState } from '@shared/store/global/global.model';

export type PrefixedRequestMethods<Prop extends string> = {
  [K in Prop as `connect${Prop}$`]: (trialId: string) => void;
} & {
  [K in Prop as `refresh${K}`]: () => void;
};
export type PrefixedRequestStates<Prop extends string> = {
  [K in Prop as `${K}Loading`]: boolean;
};

type InnerRequest = Observable<Partial<GlobalState>>;

type Store = { computed: {}; methods: {}; signals: {}; state: { selectedTrialId: string } };

// overloading the output type to include the methods
export function withTrialIdRequest<Prop extends string>(
  key: Prop,
  fn: (trialId: string, gqlService: GqlService) => InnerRequest
): SignalStoreFeature<
  Store,
  {
    computed: {};
    methods: PrefixedRequestMethods<Prop>;
    signals: {};
    state: PrefixedRequestStates<Prop>;
  }
>;
// eslint-disable-next-line no-redeclare
export function withTrialIdRequest<Prop extends string>(
  key: Prop,
  fn: (trialId: string, gqlService: GqlService) => InnerRequest
): SignalStoreFeature<Store> {
  const { connect, refresh } = getRequestMethodsKeys(key);
  const { loading } = getRequestStatesKeys(key);

  return signalStoreFeature(
    {
      state: { selectedTrialId: type<string>() },
    },
    // withState is overriding the initial state type so ignore it here
    withState({ [loading]: false }) as unknown as SignalStoreFeature<Store>,
    withMethods((store, gqlService = inject(GqlService)) => ({
      [connect]: rxMethod<string>(
        pipe(
          distinctUntilChanged(),
          filter((trialId) => !!trialId),
          tap(() => patchState(store, () => ({ [loading]: true }))),
          switchMap((trialId) => fn(trialId, gqlService)),
          tap((state) =>
            patchState(store, () => ({
              ...state,
              [loading]: false,
            }))
          )
        )
      ),
      async [refresh]() {
        patchState(store, () => ({
          [loading]: true,
        }));
        const state = await firstValueFrom(fn(store.selectedTrialId(), gqlService));

        patchState(store, () => ({
          ...state,
          [loading]: false,
        }));
      },
    })),
    withHooks({
      onInit(store) {
        effect(() => {
          void store[`connect${key}$`](store.selectedTrialId());
        });
      },
    })
  );
}

function getRequestMethodsKeys(prefix: string) {
  return {
    connect: `connect${prefix}$`,
    refresh: `refresh${prefix}`,
  };
}

function getRequestStatesKeys(prefix: string) {
  return {
    loading: `${prefix}Loading`,
  };
}
